import { FieldShell } from '../FieldShell'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import type { TextareaConfig } from '../types'
import { sanitizeRichText } from '../richText'
import { useEditorHtml } from '../useEditorHtml'
import { controlClass, useControlMeta } from './useControlMeta'

export function TextareaField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const { enableRichText, richTextAdapter } = useFieldContext()
  const config = field.config as TextareaConfig | undefined

  // Rich text is on when the host supplies an adapter AND either the form-level
  // flag (legacy alpaca parity: all textareas) or the per-field `config.rich_text`
  // opt-in is set; without an adapter we fall back to the plain textarea (same
  // graceful degradation as uploads without an uploadAdapter).
  const richText = richTextAdapter != null && (enableRichText === true || config?.rich_text === true)
  const html = value == null ? '' : String(value)
  // Plain text (an assistant's fill, a newer request) shows as paragraphs; the
  // editor's own output goes back to it untouched, or it loses the caret (#42514).
  const [editorHtml, handleEditorChange] = useEditorHtml(html, (next) =>
    // Sanitize before storing so the stored value is always clean,
    // independent of the adapter's own handling.
    meta.setFieldValue(path, sanitizeRichText(next)),
  )

  if (field.display_only) {
    return (
      <FieldShell field={field} path={path} errors={errors}>
        {richText ? (
          <div
            id={meta.id}
            className="form-control-plaintext"
            // toEditorHtml sanitizes HTML and gives plain text its paragraphs.
            dangerouslySetInnerHTML={{ __html: editorHtml }}
          />
        ) : (
          <div id={meta.id} className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>
            {html}
          </div>
        )}
      </FieldShell>
    )
  }

  if (richText) {
    const RichTextEditor = richTextAdapter
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <RichTextEditor
          id={meta.id}
          value={editorHtml}
          disabled={meta.disabled}
          placeholder={config?.placeholder}
          ariaInvalid={meta.ariaInvalid}
          ariaRequired={meta.ariaRequired}
          ariaDescribedBy={meta.describedBy}
          // The editor is no native control, so the label names it by id.
          ariaLabelledBy={meta.labelId}
          onChange={handleEditorChange}
          onBlur={() => meta.notifyBlur(path)}
        />
      </FieldShell>
    )
  }

  return (
    <FieldShell field={field} path={path} errors={errors}>
      <textarea
        id={meta.id}
        className={controlClass('form-control', meta.classNames.control, meta.hasErrors)}
        value={html}
        rows={config?.rows}
        cols={config?.cols}
        placeholder={config?.placeholder}
        disabled={meta.disabled}
        aria-invalid={meta.ariaInvalid}
        aria-required={meta.ariaRequired}
        aria-describedby={meta.describedBy}
        onChange={(event) => meta.setFieldValue(path, event.target.value)}
        onBlur={() => meta.notifyBlur(path)}
      />
    </FieldShell>
  )
}
